import { Box, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import StackedLineChartOutlinedIcon from '@mui/icons-material/StackedLineChartOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import PieChartOutlinedIcon from '@mui/icons-material/PieChartOutlined';
import AlignHorizontalLeftOutlinedIcon from '@mui/icons-material/AlignHorizontalLeftOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import { ChartType } from 'constant/ChartViewConstant';

export function getChartLabelByType(type: string) {
  switch (type) {
    case ChartType.LINE:
      return 'Line Chart';
    case ChartType.BAR:
      return 'Bar Chart';
    case ChartType.PIE:
      return 'Pie Chart';
    case ChartType.ROW:
      return 'Row Chart';
    case ChartType.GAUGE:
      return 'Gauge Chart';
    default:
      return '';
  }
}

export function getChartIconByType(type: string) {
  switch (type) {
    case ChartType.LINE:
      return (
        <StackedLineChartOutlinedIcon
          fontSize='medium'
          sx={{
            color: '#42BB93',
          }}
        />
      );
    case ChartType.BAR:
      return (
        <BarChartOutlinedIcon
          fontSize='medium'
          sx={{
            color: '#42BB93',
          }}
        />
      );
    case ChartType.PIE:
      return (
        <PieChartOutlinedIcon
          fontSize='medium'
          sx={{
            color: '#42BB93',
          }}
        />
      );
    case ChartType.ROW:
      return (
        <AlignHorizontalLeftOutlinedIcon
          fontSize='medium'
          sx={{
            color: '#42BB93',
          }}
        />
      );
    case ChartType.GAUGE:
      return (
        <SpeedOutlinedIcon
          fontSize='medium'
          sx={{
            color: '#42BB93',
          }}
        />
      );
    default:
      return null;
  }
}

// ── B. Mini Chart Previews ──

function MiniBarChart() {
  const bars = [45, 70, 35, 85, 55, 65, 40];
  return (
    <div className='chart-mini-preview'>
      {bars.map((h, i) => (
        <div
          key={i}
          className='chart-mini-bar'
          style={{ height: `${h}%`, animationDelay: `${i * 0.05}s` }}
        />
      ))}
    </div>
  );
}

function MiniLineChart() {
  const points = '10,38 25,22 40,30 55,12 70,20 85,8 100,16';
  return (
    <svg className='chart-mini-line-svg' viewBox='0 0 110 44' preserveAspectRatio='none'>
      <polyline
        points={points}
        fill='none'
        stroke='#42BB93'
        strokeWidth='2.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <polyline
        points={`10,44 ${points} 100,44`}
        fill='url(#lineGrad)'
        stroke='none'
      />
      <defs>
        <linearGradient id='lineGrad' x1='0' y1='0' x2='0' y2='1'>
          <stop offset='0%' stopColor='#42BB93' stopOpacity='0.25' />
          <stop offset='100%' stopColor='#42BB93' stopOpacity='0.02' />
        </linearGradient>
      </defs>
    </svg>
  );
}

function MiniPieChart() {
  return (
    <svg className='chart-mini-pie-svg' viewBox='0 0 36 36'>
      <circle cx='18' cy='18' r='15.9' fill='none' stroke='#42BB93' strokeWidth='3' strokeDasharray='40 60' strokeDashoffset='25' />
      <circle cx='18' cy='18' r='15.9' fill='none' stroke='#3B4797' strokeWidth='3' strokeDasharray='30 70' strokeDashoffset='85' />
      <circle cx='18' cy='18' r='15.9' fill='none' stroke='#FFC107' strokeWidth='3' strokeDasharray='30 70' strokeDashoffset='55' />
    </svg>
  );
}

function MiniRowChart() {
  const rows = [80, 55, 90, 40, 70];
  return (
    <Stack width='100%' height='100%' justifyContent='center' gap='3px' px={1}>
      {rows.map((w, i) => (
        <Box key={i} sx={{ height: 5, width: `${w}%`, bgcolor: '#42BB93', borderRadius: 1, opacity: 0.6 + i * 0.08 }} />
      ))}
    </Stack>
  );
}

function MiniGaugeChart() {
  return (
    <svg className='chart-mini-gauge-svg' viewBox='0 0 80 45'>
      <path d='M 10 40 A 30 30 0 0 1 70 40' fill='none' stroke='#e0e0e0' strokeWidth='5' strokeLinecap='round' />
      <path d='M 10 40 A 30 30 0 0 1 45 11' fill='none' stroke='#42BB93' strokeWidth='5' strokeLinecap='round' />
      <path d='M 45 11 A 30 30 0 0 1 62 22' fill='none' stroke='#FFC107' strokeWidth='5' strokeLinecap='round' />
      <path d='M 62 22 A 30 30 0 0 1 70 40' fill='none' stroke='#F44336' strokeWidth='5' strokeLinecap='round' />
      <line x1='40' y1='40' x2='50' y2='18' stroke='#333' strokeWidth='1.5' strokeLinecap='round' />
      <circle cx='40' cy='40' r='2.5' fill='#333' />
    </svg>
  );
}

export function getMiniChartByType(type: string) {
  switch (type) {
    case ChartType.BAR:
      return <MiniBarChart />;
    case ChartType.LINE:
      return <MiniLineChart />;
    case ChartType.PIE:
      return <MiniPieChart />;
    case ChartType.ROW:
      return <MiniRowChart />;
    case ChartType.GAUGE:
      return <MiniGaugeChart />;
    default:
      return null;
  }
}

export function ChartItemComponent({
  chartType,
  onEdit,
  onDelete,
  title,
  isSelected,
}: {
  chartType: string;
  title: string;
  onEdit?: () => void;
  onDelete?: () => void;
  isSelected?: boolean;
}) {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#ffffff',
        border: isSelected ? '2px solid #3B4797' : '1px solid #ccc',
        borderRadius: 2,
        boxShadow: isSelected ? '0 0 0 3px rgba(59,71,151,0.12)' : 'none',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      }}
    >
      <Stack p={1.5} height='100%' justifyContent='space-between'>
        <Stack>
          <Stack flexDirection='row' justifyContent='space-between' alignItems='center'>
            <Stack flexDirection='row' alignItems='center' gap={0.5}>
              {getChartIconByType(chartType)}
              <Typography variant='input-label-gray' component='h3' fontWeight='bold' sx={{ color: '#000', fontSize: 12 }}>
                {title}
              </Typography>
            </Stack>
            <Stack flexDirection='row' alignItems='center'>
              {onEdit && (
                <Tooltip title='Edit' arrow>
                  <IconButton
                    onClick={onEdit}
                    aria-label='edit chart'
                    size='small'
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <ModeEditOutlineOutlinedIcon fontSize='inherit' />
                  </IconButton>
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip title='Delete' arrow>
                  <IconButton
                    onClick={onDelete}
                    aria-label='delete chart'
                    size='small'
                    sx={{ color: '#e05555', '&:hover': { color: '#c62828', bgcolor: 'rgba(198,40,40,0.08)' } }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <DeleteOutlineOutlinedIcon fontSize='inherit' />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
          <Divider />
        </Stack>
        <Stack justifyContent='center' alignItems='center' flex={1} py={0.5}>
          {getMiniChartByType(chartType)}
        </Stack>
      </Stack>
    </Box>
  );
}
